import { IconTicket } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import TeeBadge from '@/components/ui/tee-badge';
import { TICKET_PILL_TYPE } from '@/pages/support-tickets/components/TicketListPane';
import type { IntakeTicketConfirmation } from '@/types/intake';

export interface EscalationConfirmationStepProps {
  ticket: IntakeTicketConfirmation;
}

// Copied verbatim from TicketDetailPane.tsx's sectionHeading constant.
const sectionHeading =
  'text-[12px] font-bold uppercase tracking-[.08em] text-[var(--text-content-sub-title)]';

/** ESCFORM-04: ticket ref, status, always-visible severity, verbatim "what HQ receives" summary. */
export function EscalationConfirmationStep({ ticket }: EscalationConfirmationStepProps) {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col items-center justify-center gap-2 text-center">
        <IconTicket size={40} className="text-[var(--color-forest)]" />
        <h2 className="text-[20px] font-semibold leading-[1.2] text-[var(--text-content-title)]">
          {t('intake.escalation.heading')}
        </h2>
      </div>

      <div className="flex flex-col gap-1 text-center">
        <span className={sectionHeading}>{t('intake.escalation.ticket_label')}</span>
        <p className="text-[28px] font-semibold leading-[1.2] text-[var(--color-forest)]">
          #{ticket.ticketNumber}
        </p>
      </div>

      <div className="flex items-center justify-between gap-3">
        <span className="text-[14px] font-semibold leading-[1.4] text-[var(--text-content-title)]">
          {t('intake.escalation.status_label')}
        </span>
        <TeeBadge
          size="s"
          type={TICKET_PILL_TYPE[ticket.status]}
          label={t(`supportTickets.filters.${ticket.status}`)}
        />
      </div>

      <div className="flex items-center justify-between gap-3">
        <span className="text-[14px] font-semibold leading-[1.4] text-[var(--text-content-title)]">
          {t('intake.escalation.severity_label')}
        </span>
        <TeeBadge
          size="s"
          type={ticket.severity === 'critical' ? 'red' : 'grey'}
          label={t(`intake.severity_${ticket.severity}`)}
        />
      </div>

      <div className="rounded-[10px] border border-[var(--border-default)] bg-[var(--background-card)] px-4 py-4 flex flex-col gap-2">
        <span className={sectionHeading}>{t('intake.escalation.hq_receives')}</span>
        <p className="whitespace-pre-line text-[15px] leading-[1.5] text-[var(--text-content-title)]">
          {ticket.summary}
        </p>
      </div>
    </div>
  );
}

export default EscalationConfirmationStep;
